/**
 * Blockchain Stats Fallback
 *
 * Provides fallback data sources when primary APIs (sidewinder s08) are unavailable.
 *
 * Primary: sidewinder s08 (CryptoCompare via 2140data.net)
 * Fallback 1: Diamondback blockchain stats (our own service - calculated from block data)
 * Fallback 2: Blockchair API (comprehensive stats)
 * Fallback 3: Blockchain.info API (basic stats)
 *
 * Data provided:
 * - transactions24h: 24-hour transaction count
 * - activeAddresses: Active addresses (24h or total depending on source)
 * - newAddresses: New addresses created
 * - hashrate: Network hashrate
 */

const DIAMONDBACK_NETWORK_STATS = 'https://2140data.com/api/v1/stats/network?period=144';
const BLOCKCHAIR_API = 'https://api.blockchair.com/bitcoin/stats';
const BLOCKCHAIN_INFO_API = 'https://api.blockchain.info/stats';
const BLOCKCHAIN_INFO_TX_COUNT = 'https://blockchain.info/q/24hrtransactioncount';

// Cache to prevent excessive API calls
let cachedFallbackData = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 60000; // 1 minute cache

/**
 * Check if s08 data is valid (not rate limited)
 */
export const isS08DataValid = (s08Data) => {
  if (!s08Data?.blockchain?.Data) return false;

  const data = s08Data.blockchain.Data;

  // Check for rate limit error
  if (s08Data.blockchain.Message?.includes('rate limit')) return false;
  if (s08Data.blockchain.Response === 'Error') return false;

  // Check if data is actually populated
  if (typeof data !== 'object' || Object.keys(data).length === 0) return false;

  // Verify key fields exist
  return data.transaction_count != null || data.active_addresses != null;
};

/**
 * Fetch from Diamondback network stats (our own service)
 * Returns: 24h network statistics calculated from block data
 */
const fetchDiamondback = async () => {
  try {
    const response = await fetch(DIAMONDBACK_NETWORK_STATS);
    if (!response.ok) throw new Error(`Diamondback error: ${response.status}`);

    const data = await response.json();

    if (!data?.stats) throw new Error('No stats from Diamondback');

    const { stats, blockRange } = data;

    return {
      source: 'diamondback',
      transactions24h: stats.totalTxCount,
      transactionsActual: stats.totalTxCount,
      activeAddresses: null, // Diamondback doesn't track addresses
      hashrate: stats.hashrate ? stats.hashrate * 1e18 : null, // Convert EH/s to H/s
      difficulty: stats.difficulty,
      avgBlockTime: stats.avgBlockTime,
      avgFeeRate: stats.avgFeeRate,
      medianFeeRate: stats.medianFeeRate,
      blockHeight: blockRange?.end || null
    };
  } catch (err) {
    console.warn('[BlockchainStatsFallback] Diamondback fetch failed:', err.message);
    return null;
  }
};

/**
 * Fetch from Blockchair API
 * Returns: transactions_24h, hodling_addresses (as proxy for active)
 */
const fetchBlockchair = async () => {
  try {
    const response = await fetch(BLOCKCHAIR_API);
    if (!response.ok) throw new Error(`Blockchair API error: ${response.status}`);

    const json = await response.json();
    const data = json?.data;

    if (!data) throw new Error('No data from Blockchair');

    return {
      source: 'blockchair',
      transactions24h: data.transactions_24h,
      // Blockchair doesn't have active_addresses, use hodling_addresses as alternative
      // Note: This is total addresses with balance, not 24h active
      activeAddresses: null, // We'll try blockchain.info for this
      hodlingAddresses: data.hodling_addresses,
      newAddresses: null,
      hashrate: data.hashrate_24h ? parseInt(data.hashrate_24h) : null,
      difficulty: data.difficulty,
      mempoolTxCount: data.mempool_transactions,
      mempoolSize: data.mempool_size
    };
  } catch (err) {
    console.warn('[BlockchainStatsFallback] Blockchair fetch failed:', err.message);
    return null;
  }
};

/**
 * Fetch from Blockchain.info API
 * Returns: n_tx (total, not 24h specific in this endpoint)
 */
const fetchBlockchainInfo = async () => {
  try {
    const response = await fetch(BLOCKCHAIN_INFO_API);
    if (!response.ok) throw new Error(`Blockchain.info API error: ${response.status}`);

    const data = await response.json();

    return {
      source: 'blockchain.info',
      // n_tx in this endpoint is recent transactions, not exactly 24h
      transactions24h: data.n_tx,
      hashrate: data.hash_rate,
      difficulty: data.difficulty,
      marketPrice: data.market_price_usd
    };
  } catch (err) {
    console.warn('[BlockchainStatsFallback] Blockchain.info fetch failed:', err.message);
    return null;
  }
};

/**
 * Fetch 24h transaction count from Blockchain.info simple query
 */
const fetchBlockchainInfoTxCount = async () => {
  try {
    const response = await fetch(BLOCKCHAIN_INFO_TX_COUNT);
    if (!response.ok) throw new Error(`Blockchain.info TX count error: ${response.status}`);

    const count = await response.text();
    return parseInt(count.trim(), 10);
  } catch (err) {
    console.warn('[BlockchainStatsFallback] Blockchain.info TX count failed:', err.message);
    return null;
  }
};

/**
 * Get fallback blockchain stats
 * Tries multiple sources and merges best available data
 */
export const getFallbackBlockchainStats = async () => {
  // Return cached data if still valid
  if (cachedFallbackData && Date.now() - cacheTimestamp < CACHE_DURATION) {
    return cachedFallbackData;
  }

  console.log('[BlockchainStatsFallback] Fetching fallback data...');

  // Fetch from all sources in parallel (Diamondback first as primary fallback)
  const [diamondbackData, blockchairData, blockchainInfoData, txCount] = await Promise.all([
    fetchDiamondback(),
    fetchBlockchair(),
    fetchBlockchainInfo(),
    fetchBlockchainInfoTxCount()
  ]);

  // Merge data, preferring Diamondback (our service), then Blockchair, then Blockchain.info
  const mergedData = {
    source: 'fallback',
    sources: [],
    // Prefer Diamondback for 24h transactions (calculated from our block data)
    transactions24h: diamondbackData?.transactions24h ?? blockchairData?.transactions24h ?? txCount ?? blockchainInfoData?.transactions24h,
    // Active addresses - only Blockchair has this (as hodling addresses)
    activeAddresses: null,
    // Hodling addresses from blockchair (addresses with balance)
    hodlingAddresses: blockchairData?.hodlingAddresses ?? null,
    // Hashrate - prefer Diamondback (calculated), then Blockchair
    hashrate: diamondbackData?.hashrate ?? blockchairData?.hashrate ?? blockchainInfoData?.hashrate,
    // Difficulty
    difficulty: diamondbackData?.difficulty ?? blockchairData?.difficulty ?? blockchainInfoData?.difficulty,
    // Mempool
    mempoolTxCount: blockchairData?.mempoolTxCount ?? null,
    mempoolSize: blockchairData?.mempoolSize ?? null,
    // Block metrics from Diamondback
    avgBlockTime: diamondbackData?.avgBlockTime ?? null,
    avgFeeRate: diamondbackData?.avgFeeRate ?? null,
    medianFeeRate: diamondbackData?.medianFeeRate ?? null,
    blockHeight: diamondbackData?.blockHeight ?? null,
    // Timestamp
    fetchedAt: Date.now()
  };

  if (diamondbackData) mergedData.sources.push('diamondback');
  if (blockchairData) mergedData.sources.push('blockchair');
  if (blockchainInfoData) mergedData.sources.push('blockchain.info');
  if (txCount) mergedData.sources.push('blockchain.info-txcount');

  // Cache the result
  cachedFallbackData = mergedData;
  cacheTimestamp = Date.now();

  console.log('[BlockchainStatsFallback] Merged data from:', mergedData.sources.join(', '));

  return mergedData;
};

/**
 * Get blockchain stats with automatic fallback
 * Uses s08 data if valid, otherwise falls back to external APIs
 *
 * @param {Object} s08Data - The s08 data from sidewinder
 * @returns {Object} Normalized blockchain stats
 */
export const getBlockchainStatsWithFallback = async (s08Data) => {
  // Try s08 first
  if (isS08DataValid(s08Data)) {
    const data = s08Data.blockchain.Data;
    return {
      source: 'sidewinder',
      transactions24h: data.transaction_count,
      activeAddresses: data.active_addresses,
      newAddresses: data.new_addresses,
      hashrate: data.hashrate,
      difficulty: data.difficulty,
      blockHeight: data.block_height,
      blockTime: data.block_time,
      blockSize: data.block_size
    };
  }

  // s08 unavailable or rate limited, use fallbacks
  console.warn('[BlockchainStatsFallback] s08 data invalid, using fallback sources');
  return await getFallbackBlockchainStats();
};

/**
 * Synchronous check and merge for existing data
 * Uses cached fallback if available, doesn't trigger new fetch
 */
export const mergeWithFallbackSync = (s08Data) => {
  // If s08 is valid, use it
  if (isS08DataValid(s08Data)) {
    return {
      transactions24h: s08Data.blockchain.Data.transaction_count,
      activeAddresses: s08Data.blockchain.Data.active_addresses,
      source: 'sidewinder'
    };
  }

  // Use cached fallback if available
  if (cachedFallbackData) {
    return {
      transactions24h: cachedFallbackData.transactions24h,
      activeAddresses: cachedFallbackData.activeAddresses,
      hodlingAddresses: cachedFallbackData.hodlingAddresses,
      source: cachedFallbackData.source
    };
  }

  // No data available
  return {
    transactions24h: null,
    activeAddresses: null,
    source: null
  };
};

/**
 * Preload fallback data
 * Call this on app init to have fallback ready
 */
export const preloadFallbackData = async () => {
  try {
    await getFallbackBlockchainStats();
    console.log('[BlockchainStatsFallback] Fallback data preloaded');
  } catch (err) {
    console.warn('[BlockchainStatsFallback] Preload failed:', err.message);
  }
};

const blockchainStatsFallback = {
  isS08DataValid,
  getFallbackBlockchainStats,
  getBlockchainStatsWithFallback,
  mergeWithFallbackSync,
  preloadFallbackData
};

export default blockchainStatsFallback;
